import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ChevronRight, Search, Eye } from 'lucide-react';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23Orders = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const tabs = [
        { value: 'all', label: 'All Orders' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
    ];

    const orders = [
        {
            id: 'UTS-2026-001',
            date: 'March 15, 2026',
            status: 'delivered',
            statusLabel: 'Delivered',
            occasion: 'Wedding',
            total: 45999,
            items: [
                {
                    name: 'Bridal Silk Lehenga',
                    size: 'M',
                    price: 45999,
                    image: 'https://images.pexels.com/photos/8881954/pexels-photo-8881954.jpeg?auto=compress&cs=tinysrgb&w=400',
                },
            ],
        },
        {
            id: 'UTS-2026-002',
            date: 'March 10, 2026',
            status: 'shipped',
            statusLabel: 'Shipped',
            occasion: 'Sangeet',
            total: 18500,
            items: [
                {
                    name: 'Sangeet Anarkali Set',
                    size: 'S',
                    price: 18500,
                    image: 'https://images.pexels.com/photos/7123307/pexels-photo-7123307.jpeg?auto=compress&cs=tinysrgb&w=400',
                },
            ],
        },
        {
            id: 'UTS-2026-003',
            date: 'March 5, 2026',
            status: 'processing',
            statusLabel: 'Processing',
            occasion: 'Festive',
            total: 19799,
            items: [
                {
                    name: 'Festive Zari Saree',
                    size: 'Free',
                    price: 12999,
                    image: 'https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=400',
                },
                {
                    name: 'Diwali Kurta Set',
                    size: 'L',
                    price: 6800,
                    image: 'https://images.pexels.com/photos/17611651/pexels-photo-17611651.jpeg?auto=compress&cs=tinysrgb&w=400',
                },
            ],
        },
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'delivered': return { bg: '#E8F5E9', text: '#1B3A2D' };
            case 'shipped': return { bg: '#FFF9F0', text: '#C5974B' };
            case 'processing': return { bg: '#F8F3EC', text: '#7A1B3B' };
            default: return { bg: '#F8F3EC', text: '#5A5A5A' };
        }
    };

    const filteredOrders = orders.filter(order => {
        const matchesTab = activeTab === 'all' || order.status === activeTab;
        const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.items.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesTab && matchesSearch;
    });

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');

                .utsav-orders-tabs {
                    display: flex;
                    gap: 8px;
                    overflow-x: auto;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .utsav-orders-tabs::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={0} />

                <div style={{
                    maxWidth: '900px',
                    margin: '0 auto',
                    padding: '120px 24px 80px',
                }}>
                    <h1 style={{
                        fontFamily: "'Libre Baskerville', Georgia, serif",
                        fontSize: 'clamp(24px, 3vw, 32px)',
                        fontWeight: 700,
                        color: '#1B3A2D',
                        margin: '0 0 8px 0',
                    }}>
                        My Orders
                    </h1>
                    <p style={{
                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                        fontSize: '15px',
                        color: '#5A5A5A',
                        margin: '0 0 32px 0',
                    }}>
                        Track your festive wear orders and delivery status
                    </p>

                    {/* Search + Tabs */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        marginBottom: '32px',
                    }}>
                        {/* Search */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            border: '1px solid #E8E0D4',
                            borderRadius: '10px',
                            padding: '0 16px',
                            backgroundColor: '#FFFFFF',
                        }}>
                            <Search size={18} color="#5A5A5A" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search orders by ID or product name..."
                                style={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    backgroundColor: 'transparent',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    color: '#1B3A2D',
                                    padding: '14px 0',
                                }}
                            />
                        </div>

                        {/* Tabs */}
                        <div className="utsav-orders-tabs">
                            {tabs.map(tab => (
                                <button
                                    key={tab.value}
                                    onClick={() => setActiveTab(tab.value)}
                                    style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        padding: '8px 20px',
                                        backgroundColor: activeTab === tab.value ? '#1B3A2D' : 'transparent',
                                        color: activeTab === tab.value ? '#FFFFFF' : '#1B3A2D',
                                        border: activeTab === tab.value ? '1px solid #1B3A2D' : '1px solid #E8E0D4',
                                        borderRadius: '24px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Orders List */}
                    {filteredOrders.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 24px',
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            boxShadow: '0 2px 12px rgba(27, 58, 45, 0.06)',
                        }}>
                            <Package size={48} color="#E8E0D4" style={{ marginBottom: '16px' }} />
                            <h3 style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: '20px',
                                color: '#1B3A2D',
                                margin: '0 0 8px 0',
                            }}>
                                No Orders Found
                            </h3>
                            <p style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '14px',
                                color: '#5A5A5A',
                                margin: '0 0 24px 0',
                            }}>
                                Start your festive shopping journey today
                            </p>
                            <button
                                onClick={() => navigate('/template23/shop')}
                                style={{
                                    padding: '12px 32px',
                                    backgroundColor: '#C5974B',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}
                            >
                                Shop Now
                            </button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {filteredOrders.map(order => {
                                const statusColor = getStatusColor(order.status);
                                return (
                                    <div
                                        key={order.id}
                                        style={{
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 12px rgba(27, 58, 45, 0.06)',
                                        }}
                                    >
                                        {/* Order Header */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '16px 20px',
                                            backgroundColor: '#F8F3EC',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                <span style={{
                                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    color: '#1B3A2D',
                                                }}>
                                                    {order.id}
                                                </span>
                                                <span style={{
                                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    color: '#C5974B',
                                                    backgroundColor: '#FFF9F0',
                                                    padding: '3px 10px',
                                                    borderRadius: '4px',
                                                    border: '1px solid rgba(197, 151, 75, 0.2)',
                                                }}>
                                                    {order.occasion}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span style={{
                                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                    fontSize: '13px',
                                                    color: '#5A5A5A',
                                                }}>
                                                    {order.date}
                                                </span>
                                                <span style={{
                                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.5px',
                                                    textTransform: 'uppercase',
                                                    color: statusColor.text,
                                                    backgroundColor: statusColor.bg,
                                                    padding: '4px 12px',
                                                    borderRadius: '4px',
                                                }}>
                                                    {order.statusLabel}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Order Items */}
                                        <div style={{ padding: '20px' }}>
                                            {order.items.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: 'flex',
                                                        gap: '16px',
                                                        alignItems: 'center',
                                                        paddingBottom: idx < order.items.length - 1 ? '16px' : 0,
                                                        marginBottom: idx < order.items.length - 1 ? '16px' : 0,
                                                        borderBottom: idx < order.items.length - 1 ? '1px solid #F8F3EC' : 'none',
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '64px',
                                                        height: '80px',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        flexShrink: 0,
                                                        backgroundColor: '#F8F3EC',
                                                    }}>
                                                        <img
                                                            src={item.image}
                                                            alt={item.name}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{
                                                            fontFamily: "'Libre Baskerville', Georgia, serif",
                                                            fontSize: '15px',
                                                            fontWeight: 400,
                                                            color: '#1B3A2D',
                                                            margin: '0 0 4px 0',
                                                        }}>
                                                            {item.name}
                                                        </h4>
                                                        <p style={{
                                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                            fontSize: '13px',
                                                            color: '#5A5A5A',
                                                            margin: 0,
                                                        }}>
                                                            Size: {item.size}
                                                        </p>
                                                    </div>
                                                    <span style={{
                                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                        fontSize: '15px',
                                                        fontWeight: 700,
                                                        color: '#C5974B',
                                                    }}>
                                                        {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Order Footer */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '16px 20px',
                                            borderTop: '1px solid #F8F3EC',
                                        }}>
                                            <div>
                                                <span style={{
                                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                    fontSize: '13px',
                                                    color: '#5A5A5A',
                                                }}>Total: </span>
                                                <span style={{
                                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                                    fontSize: '18px',
                                                    fontWeight: 700,
                                                    color: '#1B3A2D',
                                                }}>
                                                    {'\u20B9'}{order.total.toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                            <button
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '8px 16px',
                                                    backgroundColor: '#1B3A2D',
                                                    color: '#FFFFFF',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Eye size={14} />
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23Orders;
